import { getMaintenanceScheduleQueryOptions } from "@packages/api-client/hooks/equipmentController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CalendarClock, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useFarmScope } from "@/hooks/useFarmScope";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { ScheduleList } from "./-components/ScheduleList";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/schedule/",
)({
	// Maintenance planning is desk work — online-only by design.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getMaintenanceScheduleQueryOptions(params.orgId),
		);
	},
	component: SchedulePage,
});

function SchedulePage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const scope = useFarmScope();

	const { data: scheduleData, isLoading } = useQuery(
		getMaintenanceScheduleQueryOptions(orgId, {
			establishmentId:
				scope.kind === "single" ? scope.establishmentId : undefined,
		}),
	);

	const summary = scheduleData?.summary;

	return (
		<div className="flex flex-col gap-4 h-full">
			{/* Summary Badges */}
			{summary && (
				<div className="flex flex-wrap gap-2">
					{summary.overdueCount > 0 && (
						<Badge variant="destructive" className="gap-1 text-sm py-1 px-3">
							<AlertTriangle className="h-3.5 w-3.5" />
							{summary.overdueCount} {t("equipment.schedule.overdue")}
						</Badge>
					)}
					{summary.dueThisWeekCount > 0 && (
						<Badge
							variant="secondary"
							className="gap-1 text-sm py-1 px-3 bg-warning/10 text-warning border-warning/30"
						>
							<CalendarClock className="h-3.5 w-3.5" />
							{summary.dueThisWeekCount} {t("equipment.schedule.dueThisWeek")}
						</Badge>
					)}
					{summary.upcomingCount > 0 && (
						<Badge
							variant="secondary"
							className="gap-1 text-sm py-1 px-3 bg-success/10 text-success border-success/30"
						>
							<CheckCircle2 className="h-3.5 w-3.5" />
							{summary.upcomingCount} {t("equipment.schedule.upcoming")}
						</Badge>
					)}
				</div>
			)}

			<div className="card bg-base-100 shadow-md flex-1">
				<div className="card-body">
					<ScheduleList
						items={scheduleData?.data ?? []}
						orgId={orgId}
						isLoading={isLoading}
					/>
				</div>
			</div>
		</div>
	);
}
